import classNames from 'classnames';
import React, { useState } from 'react';

import { ButtonGroup, IconButton, LinkButton, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../defaultConfig';
import type { AccountId, Currency } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { transactionTrackIDs } from '../../../utils/gtm/trackIDs/transaction';
import { EikaModal } from '../../EikaModal';
import { DownloadTransactionsContextMenu } from './DownloadTransactionsContextMenu';
import { SearchResetButtons } from './SearchResetButtons';
import { useTransactionFilterContext } from './TransactionFilterContext/TransactionFilterContext';
import { DesktopTransactionFilterModalContent, MobileTransactionFilterModalContent } from './modals';

import './TransactionFilter.scss';

type TransactionFilterProps = {
    title: string;
    show: boolean;
    setShow: React.Dispatch<boolean>;
    getTransactionList: () => void;
    accountId?: AccountId;
    targetCurrency?: Currency;
    showActionButtons?: boolean;
};

const TransactionFilter: React.FC<TransactionFilterProps> = ({
    title,
    show,
    setShow,
    accountId,
    getTransactionList,
    targetCurrency,
    showActionButtons = true,
}) => {
    const translation = useTranslation();
    const { featureToggles } = useConfig();
    const {
        resetSearch,
        transactionSearchPayload,
        isFilteringState: [isFiltering, setIsFiltering],
        isDirty,
    } = useTransactionFilterContext();
    const [elementToFocusOnExit, setElementToFocusOnExit] = useState<HTMLElement | null>(null);

    const closeAndReset = () => {
        resetSearch();
        setIsFiltering(false);
        setShow(false);
    };

    const onSearch = (event: React.FormEvent<unknown>) => {
        event.preventDefault();
        setShow(false);
        setIsFiltering(true);
    };

    const viewType = useViewType();
    const currency = targetCurrency ?? 'NOK';

    return (
        <>
            <div className="transaction-list__filter-wrapper">
                {!show && isFiltering && (
                    <div>
                        <SearchResetButtons
                            setShow={setShow}
                            className="transaction-list__filter-search-reset-buttons"
                            getTransactionList={getTransactionList}
                            focusElementOnExit={elementToFocusOnExit}
                            buttonRemoveLabel={translation.transactionFilter.remove}
                            buttonEditLabel={translation.transactionFilter.editFilter}
                            aria-label={translation.transactionFilter.activeFilters}
                            infoMessage={translation.transactionFilter.reservedTransactionMessage}
                            targetCurrency={currency}
                        />
                    </div>
                )}
                <div className="transaction-list__filter-title-warpper">
                    <span className="transaction-list__filter-title bold">{title}</span>
                    <div
                        className={classNames('transaction-list__filter-buttons', {
                            'show-download': featureToggles.enableTransactionDownload,
                        })}
                    >
                        {showActionButtons && (
                            <>
                                {featureToggles.enableTransactionDownload && (
                                    <DownloadTransactionsContextMenu
                                        searchParams={transactionSearchPayload}
                                        accountId={accountId}
                                    />
                                )}
                                <IconButton
                                    ref={setElementToFocusOnExit}
                                    screenReaderText={translation.transactionFilter.openModal}
                                    onClick={() => setShow(true)}
                                    icon={IconTypes.SOEK_24}
                                    data-testid="open-transaction-filter-button"
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <EikaModal
                id="transaction-filter-modal"
                open={show}
                setOpen={setShow}
                size="large"
                contentShouldStretch
                title={translation.transactionFilter.filterTransactions}
                footerClassName="transaction-filter-form"
                footerContent={
                    <ButtonGroup
                        className="transaction-search-modal__submit-wrapper"
                        primaryAction={
                            <ButtonWithSpinner
                                buttonType="positive"
                                onClick={onSearch}
                                data-track-id={transactionTrackIDs.search.id}
                                data-track-linkname={translation.transactionFilter.showHits}
                            >
                                {translation.transactionFilter.showHits}
                            </ButtonWithSpinner>
                        }
                        secondaryAction={
                            isDirty ? (
                                <StandardButton
                                    buttonType="negative"
                                    onClick={resetSearch}
                                    data-track-id={transactionTrackIDs.search.id}
                                    data-track-linkname={translation.transactionFilter.removeChosen}
                                >
                                    {translation.transactionFilter.removeChosen}
                                </StandardButton>
                            ) : (
                                <LinkButton
                                    onClick={closeAndReset}
                                    data-track-id={transactionTrackIDs.search.id}
                                    data-track-linkname={translation.transactionFilter.cancel}
                                >
                                    {translation.transactionFilter.cancel}
                                </LinkButton>
                            )
                        }
                        hideTertiaryAction={!isDirty}
                        tertiaryAction={
                            <LinkButton
                                onClick={closeAndReset}
                                data-track-id={transactionTrackIDs.search.id}
                                data-track-linkname={translation.transactionFilter.cancel}
                            >
                                {translation.transactionFilter.cancel}
                            </LinkButton>
                        }
                    />
                }
            >
                <form onSubmit={onSearch} className="transaction-filter-form">
                    {viewType === ViewType.mobile ? (
                        <MobileTransactionFilterModalContent targetCurrency={currency} />
                    ) : (
                        <DesktopTransactionFilterModalContent targetCurrency={currency} />
                    )}
                </form>
            </EikaModal>
        </>
    );
};

export default TransactionFilter;
